import { provideGalaxyDateAdapter } from '@vendasta/galaxy/datepicker';
import { ObserversModule } from '@angular/cdk/observers';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AtlasModule } from '@galaxy/atlas';
import { LexiconModule } from '@galaxy/lexicon';
import { FeatureFlagService } from '@galaxy/partner';
import { SmsContextToken, SmsInjectionToken, SmsModule } from '@galaxy/sms';
import { DEFAULT_LANGUAGE, TranslateModule, TranslateService } from '@ngx-translate/core';
import { BusinessNavModule } from '@vendasta/business-nav';
import { GalaxyI18NModule, getLocale } from '@vendasta/galaxy/i18n';
import { GalaxyDefaultProviderOverrides } from '@vendasta/galaxy/provider-default-overrides';
import { provideErrorTracking } from '@vendasta/core';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { of } from 'rxjs';
import { map, publishReplay, refCount, startWith } from 'rxjs/operators';
import baseTranslation from '../assets/i18n/en_devel.json';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AtlasComponent } from './atlas/atlas.component';
import { InternalAtlasService } from './atlas/atlas.service';
import { WEBLATE_COMPONENT_NAME } from './common/constants';
import { CoreModule } from './core/core.module';
import { CustomerFacingModule } from './customer-facing/customer-facing.module';
import { SharedModule } from './shared/shared.module';
import { SmsContextLibraryInjectionToken, SmsRegistrationLibraryInjectionToken } from './smsRegistration/providers';
import { provideProtoTransport } from '@vendasta/protos';

@NgModule({
  declarations: [AppComponent, AtlasComponent],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    BrowserModule,
    CustomerFacingModule,
    BusinessNavModule.forRoot({
      locationSwitchConfig: {
        supportedLocationContextConfig: [
          {
            context: 'location',
            locationParamKey: 'accountGroupId',
          },
        ],
        defaultPath: 'cv/account/id_placeholder/app',
        blockListedPaths: [],
      },
    }),
    AtlasModule,
    ObserversModule,
    ProductAnalyticsModule,
    TranslateModule,
    LexiconModule.forRoot({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
    RecaptchaV3Module,
    GalaxyI18NModule,
    SmsModule,
  ],
  providers: [
    provideGalaxyDateAdapter(),
    provideProtoTransport(),
    provideErrorTracking(),
    ...GalaxyDefaultProviderOverrides,
    FeatureFlagService,
    {
      provide: DEFAULT_LANGUAGE,
      useFactory: () => getLocale(),
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: '6LcghsEZAAAAAO3XqQ3AAirqlE5fuSKQo_TSBnTY',
    },
    {
      provide: 'CURRENT_LANG',
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => {
        return translateService.onLangChange.pipe(
          map((langChange) => langChange.lang),
          startWith(translateService.currentLang),
        );
      },
    },
    {
      provide: 'DEFAULT_LANG',
      useFactory: () => of('en').pipe(publishReplay(1), refCount()),
    },
    InternalAtlasService,
    {
      provide: SmsInjectionToken,
      useExisting: SmsRegistrationLibraryInjectionToken,
      deps: [],
    },
    {
      provide: SmsContextToken,
      useExisting: SmsContextLibraryInjectionToken,
      deps: [],
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
